import { ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import type { AIInsights, Cluster } from '@/services/types/api.types';
import { confirmDelete } from '@/helpers/dialogs.helper';

export function useInsightsThemeManagement(insights: Ref<AIInsights | null>, onSave: () => void) {
  const $q = useQuasar();
  const { t } = useI18n();

  const newThemeName = ref<string>('');
  const showNewThemeInput = ref(false);

  function createNewTheme() {
    const themeName = newThemeName.value.trim();

    if (!themeName) {
      $q.notify({
        type: 'warning',
        message: t('consultation.ai.themes.empty_error'),
        position: 'top',
      });
      return;
    }

    const duplicate = insights.value?.clusters?.find((c) => c.theme.toLowerCase() === themeName.toLowerCase());
    if (duplicate) {
      $q.notify({
        type: 'warning',
        message: t('consultation.ai.themes.duplicate_error'),
        position: 'top',
      });
      return;
    }

    if (!insights.value) {
      insights.value = { ideas: [], themes: [], clusters: [] };
    }
    if (!insights.value.clusters) {
      insights.value.clusters = [];
    }

    insights.value.clusters.push({
      theme: themeName,
      ideas: [],
    });

    newThemeName.value = '';
    showNewThemeInput.value = false;
    onSave();

    $q.notify({
      type: 'positive',
      message: t('consultation.ai.themes.add') + ': ' + themeName,
      position: 'top',
    });
  }

  function cancelNewTheme() {
    newThemeName.value = '';
    showNewThemeInput.value = false;
  }

  function deleteTheme(cluster: Cluster) {
    confirmDelete(t('global.entities.theme')).onOk(() => {
      const idx = insights.value?.clusters.findIndex((c) => c.theme === cluster.theme) ?? -1;
      if (idx !== -1) {
        insights.value?.clusters.splice(idx, 1);
        onSave();
      }
    });
  }

  function deleteIdea(cluster: { ideas: { representative_idea: string }[] }, idea: { representative_idea: string }) {
    confirmDelete(t('global.entities.summary')).onOk(() => {
      const ideas = cluster.ideas;
      const idx = ideas.indexOf(idea);
      if (idx !== -1) {
        ideas.splice(idx, 1);
        onSave();
      }
    });
  }

  return {
    newThemeName,
    showNewThemeInput,
    createNewTheme,
    cancelNewTheme,
    deleteTheme,
    deleteIdea,
  };
}
